import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    effect,
    inject,
    input,
    signal,
    TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, Subscription } from 'rxjs';
import { BD_LOADER_OPTIONS_DEFAULT, BdLoaderOptions } from '@bindoc-projects/platform-lib/data';
import { BdLoader, BdLoaderState, BdPromiseLoader, BdSubjectLoader } from '@bindoc-projects/platform-lib/data';
import { ProgressSpinner } from 'primeng/progressspinner';
import { NgTemplateOutlet } from '@angular/common';

export class BdLoaderTemplate {
}

@Component({
    selector: 'bd-loader',
    template: `
    <div>
      @switch (currentState()) {
        @case (BdLoaderState.LOADING) {
          @if (customLoadingTemplate()) {
            <ng-container *ngTemplateOutlet="customLoadingTemplate()">
            </ng-container>
          } @else {
            <div class="flex flex-row items-center justify-center">
              <div class="basis-24 m-6">
                <p-progressspinner
                  strokeWidth="3"
                  ariaLabel="Wird geladen"
                ></p-progressspinner>
              </div>
            </div>
          }
        }
        @case (BdLoaderState.INIT) {
          <ng-container
            *ngTemplateOutlet="
              initTemplate();
              context: { $implicit: initTemplateContext() }
            "
          >
          </ng-container>
        }
        @case (BdLoaderState.SUCCESS) {
          <ng-container
            *ngTemplateOutlet="
              successTemplate();
              context: { $implicit: successTemplateContext() }
            "
          >
          </ng-container>
        }
        @case (BdLoaderState.ERROR) {
          <ng-container
            *ngTemplateOutlet="
              errorTemplate();
              context: { $implicit: errorTemplateContext() }
            "
          >
          </ng-container>
        }
      }
    </div>
  `,
    styles: [
        `
      :host {
        --p-progressspinner-color-one: var(--p-primary-color);
        --p-progressspinner-color-two: var(--p-primary-color);
        --p-progressspinner-color-three: var(--p-primary-color);
        --p-progressspinner-color-four: var(--p-primary-color);
      }
    `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgTemplateOutlet, ProgressSpinner],
})
export class BdLoaderComponent {
    customLoadingTemplate = input<TemplateRef<BdLoaderTemplate> | null>(null);
    errorTemplate = input<TemplateRef<BdLoaderTemplate> | null>(null);
    successTemplate = input<TemplateRef<BdLoaderTemplate> | null>(null);
    initTemplate = input<TemplateRef<BdLoaderTemplate> | null>(null);
    errorTemplateContext = input<unknown>(undefined);
    successTemplateContext = input<unknown>(undefined);
    initTemplateContext = input<unknown>(undefined);
    loadItem = input<Promise<unknown> | Subject<BdLoaderState> | undefined>(undefined);
    options = input<BdLoaderOptions>(BD_LOADER_OPTIONS_DEFAULT);

    public readonly BdLoaderState = BdLoaderState;
    public readonly currentState = signal<BdLoaderState>(BdLoaderState.INIT);

    private _loader: BdLoader<Promise<unknown> | Subject<BdLoaderState>> | null | undefined;
    private _stateSubscription: Subscription | undefined;
    private readonly _destroyRef = inject(DestroyRef);

    constructor() {
        effect(() => {
            const loadItem = this.loadItem();
            if (loadItem) {
                this._onLoadItemChange(loadItem);
            }
        });

        this._destroyRef.onDestroy(() => {
            if (this._loader) {
                this._loader.destroy();
            }
            if (this._stateSubscription) {
                this._stateSubscription.unsubscribe();
            }
        });
    }

    private _onLoadItemChange(loadItem: Promise<unknown> | Subject<BdLoaderState>): void {
        if (this._loader) {
            this._loader.destroy();
            this._loader = null;
        }

        if (this._isPromise(loadItem)) {
            this._setPromiseLoader(loadItem);
        } else {
            this._setSubjectLoader(loadItem);
        }
    }

    private _setPromiseLoader(loadItem: Promise<unknown>): void {
        this._loader = new BdPromiseLoader();
        this._subscribeLoaderState(this._loader);
        this._loader.connect(loadItem);
    }

    private _setSubjectLoader(loadItem: Subject<BdLoaderState>): void {
        this._loader = new BdSubjectLoader();
        this._subscribeLoaderState(this._loader);
        this._loader.connect(loadItem);
    }

    private _subscribeLoaderState(loader: BdLoader<Promise<unknown> | Subject<BdLoaderState>>): void {
        if (this._stateSubscription) {
            this._stateSubscription.unsubscribe();
        }

        this._stateSubscription = loader.state$
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((state) => {
                this.currentState.set(state);
            });
    }

    private _isPromise(loadItem: Promise<unknown> | Subject<BdLoaderState>): loadItem is Promise<unknown> {
        return 'then' in loadItem && typeof loadItem.then === 'function';
    }
}
